<template>
  <div class="project-detail" :class="theme">
    <button @click="closeProjectDetail" 
    class="close-button" 
    :class="{ 'light-theme': isLightTheme, 'dark-theme': !isLightTheme }">×</button>

    <div class="project-header">
      <h2 v-html="formatTitle(project.projet)"></h2>
      <h3>{{ project.lenght }} - {{ project.date }}</h3>

      <div class="project-content">
        <h4>
          {{ project.format.includes('music') ? 'Réalisé par' : 'Un film de' }} 
          <span>{{ project.realisat }}</span>
        </h4>

        <template v-if="project.siteDOP">
          <h4>
            Image
            <a :href="project.siteDOP" target="_blank">{{ project.DOP }}</a>
          </h4>
        </template>
        <template v-else-if="project.DOP">
          <h4>Image <span>{{ project.DOP }}</span></h4>
        </template>

        <template v-if="project.prod && project.prod2">
          <h4 v-if="project.siteprod && project.siteprod2">
            Production
            <a :href="project.siteprod" target="_blank">{{ project.prod }}</a>
            &
            <a :href="project.siteprod2" target="_blank">{{ project.prod2 }}</a>
          </h4>
          <h4 v-else-if="project.siteprod">
            Production
            <a :href="project.siteprod" target="_blank">{{ project.prod }}</a>
            &
            <span>{{ project.prod2 }}</span>
          </h4>
          <h4 v-else>
            Production
            <span>{{ project.prod }}</span>
            &
            <span>{{ project.prod2 }}</span>
          </h4>
        </template>

        <template v-else-if="project.prod">
          <h4>
            Production
            <a v-if="project.siteprod" :href="project.siteprod" target="_blank">{{ project.prod }}</a>
            <span v-else>{{ project.prod }}</span>
          </h4>
        </template>

        <p class="synopsis">{{ project.description }}</p>
      </div>
    </div>

    <!-- Video integration -->
    <div v-if="project.video" class="video-container">
      <iframe
        v-if="isYouTubeVideo(project.video)"
        :src="getYouTubeEmbedUrl(project.video)"
        frameborder="0"
        allowfullscreen
      ></iframe>
      <iframe
        v-else-if="isVimeoVideo(project.video)"
        :src="getVimeoEmbedUrl(project.video)"
        frameborder="0"
        allowfullscreen
      ></iframe>
    </div>

    <!-- Image gallery -->
    <div class="project-gallery">
      <div class="gallery-container">
        <div
          class="gallery-pic"
          v-for="index in numberOfImages"
          :key="index"
          @click="openLightbox(index - 1)"
        >
          <img
            v-lazy-img
            :data-src="getCarouselImage(project['name-id'], index).src"
            :data-srcset="getCarouselImage(project['name-id'], index).srcset"
            sizes="(max-width: 800px) 100vw, 400px"
            class="project-image loading"
            loading="lazy"
            @load="(e) => e.target.classList.remove('loading')"
          />

        </div>
      </div>
    </div>

    <div v-if="lightboxImageIndex !== null" class="lightbox custom-cursor" @click="closeLightbox">

      <button
        class="nav-button left"
        @click.stop="setLightboxIndex(lightboxImageIndex - 1)"
        v-if="lightboxImageIndex > 0"
      >
        <i
          :class="[
            'bi bi-arrow-left-circle'
          ]"
        ></i>
      </button>
      
<img 
  :src="getCarouselImage(project['name-id'], lightboxImageIndex + 1).src"
  :srcset="getCarouselImage(project['name-id'], lightboxImageIndex + 1).srcset"
  sizes="100vw"
  class="lightbox-image"
  loading="eager"
/>


      <button
        class="nav-button right"
        @click.stop="setLightboxIndex(lightboxImageIndex + 1)"
        v-if="lightboxImageIndex < numberOfImages - 1"
      >
        <i
          :class="[
            'bi bi-arrow-right-circle'
          ]"
        ></i>
      </button>

    </div>
  </div>
</template>

<script>
import "./ProjectDetail.css";

export default {
  props: {
    project: {
      type: Object,
      required: true,
    },
    numberOfImages: {
      type: Number,
      required: true,
    },
    closeProjectDetail: {
      type: Function,
      required: true,
    },
    getCarouselImage: {
      type: Function,
      required: true,
    },
    isYouTubeVideo: {
      type: Function,
      required: true,
    },
    isVimeoVideo: {
      type: Function,
      required: true,
    },
    getYouTubeEmbedUrl: {
      type: Function,
      required: true,
    },
    getVimeoEmbedUrl: {
      type: Function,
      required: true,
    },
    theme: {
      type: String,
      required: true,
    }
  },

  data() {
    return {
      lightboxImageIndex: null,
      touchStartX: 0,
      touchEndX: 0,
    };
  },

  computed: {
    isLightTheme() {
      return this.theme === 'light';
    },
    currentLightboxImage() {
      return this.lightboxImageIndex !== null
        ? this.getCarouselImage(this.project['name-id'], this.lightboxImageIndex + 1)
        : null;
    },
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('popstate', this.handlePopState);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('popstate', this.handlePopState);
  },

  directives: {
    // lazy-load local directive
    lazyImg: {
      mounted(el, binding) {
        const placeholder = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';

        if (el.tagName === 'IMG') {
          el.src = placeholder;
        }

        const load = () => {
          const src = el.dataset.src || binding.value;
          const srcset = el.dataset.srcset;

          if (src) el.src = src;
          if (srcset) el.srcset = srcset;
        };

        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              load();
              io.unobserve(entry.target);
            }
          });
        }, { rootMargin: '300px' });

        io.observe(el);
        el._lazyIo = io;
      },

      unmounted(el) {
        if (el._lazyIo) el._lazyIo.disconnect();
      }
    }

  },

  methods: {
    formatTitle(title) {
      return title.replace(/\|/g, '<br>');
    },

    setLightboxIndex(index) {
      if (index >= 0 && index < this.numberOfImages) {
        this.lightboxImageIndex = index;
      }
    },

    openLightbox(index) {
      this.lightboxImageIndex = index;
      // Ajoute un état dans l'historique pour la lightbox ouverte
      window.history.pushState({ lightboxOpen: true }, '');

      // précharge l'image courante et les images adjacentes
      this.preloadImage(index);
      if (index - 1 >= 0) this.preloadImage(index - 1);
      if (index + 1 < this.numberOfImages) this.preloadImage(index + 1);
    },

    preloadImage(index) {
      if (index == null) return;
      const url = this.getCarouselImage(this.project['name-id'], index + 1);
      const img = new Image();
      img.src = url;
    },

    closeLightbox() {
    this.lightboxImageIndex = null;
      // Quand on ferme la lightbox, on fait un retour arrière dans l'historique
      // pour rester sur la page projet (et ne pas fermer tout le détail)
      if (window.history.state && window.history.state.lightboxOpen) {
        window.history.back();
      }
    },

  handlePopState(event) {
    // Si on revient en arrière et que la lightbox est ouverte, on la ferme sans quitter la page
    if (this.lightboxImageIndex !== null) {
      this.lightboxImageIndex = null;
    } else {
      // Sinon c'est un popstate normal, par exemple pour fermer la page détail (à gérer à l'extérieur)
      // Ou tu peux émettre un événement ici pour prévenir le parent
    }
  },

  handleKeyDown(event) {
    if (this.lightboxImageIndex !== null) {
      if (event.key === 'Escape') {
        this.closeLightbox();
      } else if (event.key === 'ArrowLeft' && this.lightboxImageIndex > 0) {
        this.setLightboxIndex(this.lightboxImageIndex - 1);
      } else if (event.key === 'ArrowRight' && this.lightboxImageIndex < this.numberOfImages - 1) {
        this.setLightboxIndex(this.lightboxImageIndex + 1);
      }
    }
  },

    handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX;
  },
  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipeGesture();
  },
  handleSwipeGesture() {
    const diffX = this.touchStartX - this.touchEndX;
    const threshold = 50; // pixels

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0 && this.lightboxImageIndex < this.numberOfImages - 1) {
        // swipe gauche → image suivante
        this.setLightboxIndex(this.lightboxImageIndex + 1);
      } else if (diffX < 0 && this.lightboxImageIndex > 0) {
        // swipe droite → image précédente
        this.setLightboxIndex(this.lightboxImageIndex - 1);
      }
    }
  },

  },
};
</script>

<style>
/* ajout style pour effet blur lors du chargement */
.project-image.loading { filter: blur(8px); transform: scale(1.02); transition: filter 300ms ease, transform 300ms ease; }
.project-image { transition: filter 300ms ease, transform 300ms ease; }
</style>
